import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, Trash2, Power, Search, Webhook as WebhookIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/components/shared/Toast';
import {
  useGetWebhooksQuery,
  useSetWebhookEnabledMutation,
  useDeleteWebhookMutation,
} from '@/store/api/communicationApi';
import { webhookIdentifier, type Webhook } from '@/types';

export function WebhooksPage() {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Webhook | null>(null);

  const { data, isLoading, isError } = useGetWebhooksQuery({ search: search || undefined });
  const webhooks = data?.data ?? [];
  const [setEnabled, { isLoading: toggling }] = useSetWebhookEnabledMutation();
  const [deleteWebhook, { isLoading: deleting }] = useDeleteWebhookMutation();

  const openDetail = (w: Webhook) => navigate(`/webhooks/${webhookIdentifier(w)}`);

  const toggle = async (w: Webhook) => {
    try {
      const id = webhookIdentifier(w);
      await setEnabled({ id, enabled: w.status !== 'active' }).unwrap();
      toastSuccess(`Webhook ${w.status === 'active' ? 'disabled' : 'enabled'}`);
    } catch {
      toastError('Failed to update webhook status');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteWebhook(webhookIdentifier(deleteTarget)).unwrap();
      toastSuccess('Webhook deleted');
      setDeleteTarget(null);
    } catch {
      toastError('Failed to delete webhook');
    }
  };

  const columns: ColumnDef<Webhook, unknown>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <WebhookIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <p className="truncate font-medium">{row.original.name}</p>
              {row.original.description && (
                <p className="truncate text-xs text-muted-foreground">{row.original.description}</p>
              )}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={row.original.status === 'active' ? 'success' : 'secondary'}>
            {row.original.status}
          </Badge>
        ),
      },
      {
        accessorKey: 'trigger.kind',
        header: 'Trigger',
        cell: ({ row }) => <Badge variant="outline">{row.original.trigger?.kind ?? '—'}</Badge>,
      },
      {
        accessorKey: 'version',
        header: 'Version',
        cell: ({ row }) => <span className="tabular-nums">{row.original.version}</span>,
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => format(new Date(row.original.createdAt), 'MMM d, yyyy'),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              title={row.original.status === 'active' ? 'Disable' : 'Enable'}
              disabled={toggling}
              onClick={(e) => {
                e.stopPropagation();
                toggle(row.original);
              }}
            >
              <Power className={row.original.status === 'active' ? 'text-destructive' : 'text-green-600'} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title="Edit"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/webhooks/${webhookIdentifier(row.original)}/edit`);
              }}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title="Delete"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(row.original);
              }}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, toggle, toggling],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Webhooks</h1>
          <p className="text-sm text-muted-foreground">
            Sandboxed event handlers with HTTP and internal-event triggers.
          </p>
        </div>
        <Button onClick={() => navigate('/webhooks/new')}>
          <Plus className="mr-2 h-4 w-4" /> New Webhook
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-0">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search webhooks..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : isError ? (
            <p className="py-8 text-center text-sm text-destructive">Failed to load webhooks.</p>
          ) : webhooks.length === 0 ? (
            <EmptyState
              title="No webhooks"
              description="Create a webhook to handle events."
              action={<Button onClick={() => navigate('/webhooks/new')}>New Webhook</Button>}
            />
          ) : (
            <DataTable columns={columns} data={webhooks} onRowClick={openDetail} />
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete webhook"
        description={`Are you sure you want to delete "${deleteTarget?.name ?? 'this webhook'}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={deleting}
      />
    </div>
  );
}